/**
 * Läden und Einkaufstage (18.10.2026).
 *
 * Eine Einkaufsliste, viele Läden: Ein Artikel trägt optional seinen Laden,
 * „egal“ steht in jeder Ladenansicht. Jeder Laden kann die Regale in seiner
 * Laufreihenfolge haben. Der Wochenplan rechnet bis zum nächsten Einkauf statt
 * fest sieben Tage.
 */

import { SHELF_CATEGORIES, itemKey } from './shopping.js';
import type { ShelfCategory } from './shopping.js';
import { addDays } from './time.js';
import type { IsoDate } from './time.js';

/** Die Regale eines Ladens, vollständig: Gespeichertes zuerst, Fehlendes in üblicher Folge. */
export function shopShelves(order: readonly string[] | null | undefined): ShelfCategory[] {
  const known = (order ?? []).filter((entry): entry is ShelfCategory =>
    (SHELF_CATEGORIES as readonly string[]).includes(entry),
  );
  const unique = [...new Set(known)];
  return [...unique, ...SHELF_CATEGORIES.filter((entry) => !unique.includes(entry))];
}

/** Ein Regal eine Stelle nach vorn (-1) oder hinten (+1). */
export function moveShelf(
  order: readonly string[] | null | undefined,
  shelf: ShelfCategory,
  step: -1 | 1,
): ShelfCategory[] {
  const list = shopShelves(order);
  const from = list.indexOf(shelf);
  const to = from + step;
  if (from < 0 || to < 0 || to >= list.length) return list;
  [list[from], list[to]] = [list[to]!, list[from]!];
  return list;
}

/**
 * Was in der Ansicht eines Ladens steht: seine Artikel und alles „egal“ —
 * auch Artikel eines gelöschten Ladens, die sonst nirgends mehr stünden.
 * `null` heißt alle Läden.
 */
export function itemsForShop<T extends { shopId?: string | null }>(
  items: readonly T[],
  shopId: string | null,
  liveShopIds: ReadonlySet<string>,
): T[] {
  if (shopId === null) return [...items];
  return items.filter(
    (item) => !item.shopId || item.shopId === shopId || !liveShopIds.has(item.shopId),
  );
}

/** In der Laufreihenfolge des Ladens, im Regal nach Namen. */
export function sortForShop<T extends { category: ShelfCategory; name: string }>(
  items: readonly T[],
  order: readonly string[] | null | undefined,
): T[] {
  const shelves = shopShelves(order);
  return [...items].sort(
    (a, b) =>
      shelves.indexOf(a.category) - shelves.indexOf(b.category) ||
      a.name.localeCompare(b.name, 'de'),
  );
}

/**
 * Der Laden, in dem man das zuletzt gekauft hat — „Zahnpasta“ steht dann
 * gleich bei dm. Ältestes zuerst übergeben ist egal; gewinnt der neueste.
 */
export function rememberedShop<
  T extends { name: string; shopId?: string | null; updatedAt: string },
>(name: string, history: readonly T[], liveShopIds: ReadonlySet<string>): string | null {
  const key = itemKey(name);
  const match = history
    .filter((item) => item.shopId && liveShopIds.has(item.shopId) && itemKey(item.name) === key)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
  return match?.shopId ?? null;
}

/**
 * Zeitraum für „Aus dem Wochenplan“: vom nächsten Einkaufstag (oder heute)
 * bis zum Tag vor dem übernächsten — ohne zweiten Einkauf eine Woche.
 */
export function tripWindow(
  today: string,
  tripDates: readonly string[],
): { from: string; to: string; nextTrip: string | null } {
  const upcoming = [...new Set(tripDates)].filter((date) => date >= today).sort();
  const from = upcoming[0] ?? today;
  const following = upcoming.find((date) => date > from) ?? null;
  return {
    from,
    to: following ? addDays(following as IsoDate, -1) : addDays(from as IsoDate, 6),
    nextTrip: following,
  };
}
